import { useEffect, useState } from 'react';
import { Slider } from '@material-ui/core';
import { useTranslation } from '../../tools/i18n';
import { makeStyles } from '@material-ui/styles';
import theme from '../theme';
import useSessionStore from '@services/stores/SessionStore';
import NewportInput from '@components/forms/NewportInput';
import { getFilterConfiguration } from '@services/FilterConfiguration';

const useStyles = makeStyles({
    wrapper: {
        marginBottom: 15,
        order: 10,
        [theme.breakpoints.down('md')]: {
            padding: '0 15px',
        },
    },
    trigger: {
        display: 'none',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 15,
        padding: '10px 12px 10px 12px',
        borderRadius: 0,
        backgroundColor: '#f5f5f5',
        cursor: 'pointer',
        '& svg': {
            '&.desktop': {
                [theme.breakpoints.down('lg')]: {
                    display: 'none',
                },
            },
            '&.mobile': {
                [theme.breakpoints.up('lg')]: {
                    display: 'none',
                },
            },
        },
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            marginTop: -1,
            marginBottom: 0,
            padding: '25px 15px 25px 15px',
            backgroundColor: theme.palette.primary.background,
            borderTop: '1px solid #e0e0e0',
            borderBottom: '1px solid #e0e0e0',
            '&.open': {
                marginBottom: 10,
                paddingBottom: 0,
                borderBottom: 'none',
            },
        },
    },
    inner: {
        marginBottom: 10,
        [theme.breakpoints.up('lg')]: {
            padding: '10px 0',
        },
    },
    label: {
        display: 'none',
        margin: '-30px 0 0 0',
        fontSize: 12,
    },
    inputWrapper: {
        display: 'grid',
        gridTemplateColumns: '80px 1fr 80px',
    },
    inputWithSymbol: {
        position: 'relative',
    },
    symbolUnit: {
        position: 'absolute',
        right: 12,
        top: 3,
        fontSize: 12,
    },
    input: {
        height: 24,
        padding: '6px 0',
        border: 0,
        borderBottom: 'dashed 1px',
        background: 'transparent !important',
        fontSize: 12,
    },
    root: (props) => ({
        paddingTop: 15,
        [theme.breakpoints.up('lg')]: {
            paddingTop: 10,
            minWidth: 200,
        },
    }),
    markLabel: {
        top: 35,
        left: '0 !important',
        transform: 'translateX(0)',
        fontSize: 12,
        [theme.breakpoints.up('sm')]: {
            top: 20,
            transform: 'translateX(0%)',
        },
        '& ~ &': {
            left: 'auto !important',
            right: '0 !important',
            transform: 'translateX(0)',
        },
    },
    thumb: {
        marginLeft: 0,
        '&[data-index="1"]': {
            transform: 'translateX(-12px)',
        },
    },
    mark: {
        display: 'none',
    },
}, { name: 'FilterRange' });

export default function FilterRange(
    {
        name,
        currentFilter,
        min,
        max,
        onChange,
    },
) {
    const sessionMethods = useSessionStore((state) => state.methods);
    const classes = useStyles();
    const { t } = useTranslation('attributes');
    const [ state, setState ] = useState({
        min: numbers((currentFilter && currentFilter.min) ? parseFloat(currentFilter.min) : min, 'min'),
        max: numbers((currentFilter && currentFilter.max) ? parseFloat(currentFilter.max) : max, 'max'),
    });
    const currencySymbol = sessionMethods.getCurrencySymbol();
    const configuration = getFilterConfiguration()?.enabledRangeFilters;
    const config = configuration?.[name];

    useEffect(() => {
        setState({
            ...state,
            min: numbers((currentFilter && currentFilter.min) ? parseFloat(currentFilter.min) : min, 'min'),
            max: numbers((currentFilter && currentFilter.max) ? parseFloat(currentFilter.max) : max, 'max'),
        });
    }, [ currentFilter, min, max ]);

    function onSliderChange(event, values) {
        setState({ min: values[0], max: values[1] });
    }

    function inputWithSymbol(element) {
        if (name === 'price') {
            return <section className={classes.inputWithSymbol}>
                {element}
                <div className={classes.symbolUnit}>{currencySymbol}</div>
            </section>;
        }

        return <section className={classes.inputWithSymbol}>
            {element}
            <div className={classes.symbolUnit}>{symbol(config)}</div>
        </section>;
    }

    function symbol(config) {
        return config?.convertTo || config.unit;
    }

    function numbers(value, mode) {
        if (Number.isNaN(parseFloat(value))) {
            return '';
        }

        value = mode === 'min' ? Math.floor(value) : Math.ceil(value);

        if (config?.unit === 'mm' && config?.convertTo === 'cm') {
            return (parseFloat(value) / 10).toFixed(0);
        }

        return parseFloat(value).toFixed(0);
    }

    function converter(value) {
        if (config?.unit === 'mm' && config?.convertTo === 'cm') {
            return value * 10;
        }

        return value;
    }

    function handleInputChange(event) {
        console.log(event.key, event.type);
        if (event.key === 'Enter' || event.type === 'blur') {
            onChange(name, { min: converter(state.min), max: converter(state.max) });
        }
    }

    if (!(min >= 0 && max > 0 && (min !== max))) {
        return null;
    }

    if (min > max) {
        return null;
    }

    return (
        <div className={classes.wrapper}>
            {t(`filters:${name}`)}
            <div className={classes.inner}>
                <Slider
                    name={name}
                    value={[ state.min || 0, state.max || 999999 ]}
                    min={parseFloat(numbers(min))}
                    max={parseFloat(numbers(max))}
                    steps={1}
                    onChange={onSliderChange}
                    onChangeCommitted={(event, values) => {
                        onChange(name, { min: converter(values[0]), max: converter(values[1]) });
                    }}
                    classes={{
                        root: classes.root,
                        markLabel: classes.markLabel,
                        mark: classes.mark,
                        thumb: classes.thumb,
                    }}
                    valueLabelDisplay="off"
                />
                <section className={classes.inputWrapper}>
                    {inputWithSymbol(
                        <NewportInput
                            className={classes.input}
                            value={state.min}
                            type={'number'}
                            onTouchStart={(event) => {
                                setState((prev) => {
                                    return { ...prev, min: '' };
                                });
                            }}
                            onChange={(event) => {
                                const value = event.target.value;
                                setState((prev) => {
                                    return {
                                        ...prev,
                                        min: value,
                                    };
                                });
                            }}
                            onKeyDown={handleInputChange}
                            onBlur={handleInputChange}
                        ></NewportInput>,
                    )}
                    <div></div>
                    {inputWithSymbol(
                        <NewportInput
                            className={classes.input}
                            value={state.max}
                            type={'number'}
                            onTouchStart={(event) => {
                                setState((prev) => {
                                    return { ...prev, max: '' };
                                });
                            }}
                            onChange={(event) => {
                                const value = event.target.value;
                                setState((prev) => {
                                    return {
                                        ...prev,
                                        max: value,
                                    };
                                });
                            }}
                            onKeyDown={handleInputChange}
                            onBlur={handleInputChange}
                        ></NewportInput>,
                    )}
                </section>
            </div>
        </div>
    );
}
